import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../config/api';

const ListAndTagDropdown = ({ selectedList, selectedTag, onListChange, onTagChange, onlyList = false, excludeList = '' }) => {
  const [wordLists, setWordLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [error, setError] = useState('');
  const listDropdownRef = useRef(null);
  const tagDropdownRef = useRef(null);

  useEffect(() => {
    fetchWordLists();
  }, []);

  useEffect(() => {
    if (selectedList && !onlyList) {
      fetchTags(selectedList);
    } else {
      setTags([]);
      if (!onlyList) {
        onTagChange('');
      }
    }
  }, [selectedList, onlyList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (listDropdownRef.current && !listDropdownRef.current.contains(event.target)) {
        setIsListOpen(false);
      }
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setIsTagOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchWordLists = async () => {
    try {
      const lists = await api.getWordLists();
      const listsExcluded = lists.filter(list => list.id !== Number(excludeList));
      setWordLists(listsExcluded);
      if (listsExcluded.length > 0 && !selectedList) {
        onListChange(listsExcluded[0].id);
      }
    } catch (err) {
      setError('Nie udało się pobrać list słówek');
    }
  };

  const fetchTags = async (listId) => {
    try {
      const tagsData = await api.getTagsByWordList(listId);
      setTags(tagsData);
      onTagChange('');
    } catch (err) {
      console.error('Nie udało się pobrać tagów:', err);
      setTags([]);
      onTagChange('');
    }
  };

  const handleListSelect = (listId) => {
    onListChange(listId);
    setIsListOpen(false);
  };

  const handleTagSelect = (tagId) => {
    onTagChange(tagId);
    setIsTagOpen(false);
  };

  const getSelectedListName = () => {
    if (!selectedList) return 'Wybierz listę słówek';
    const list = wordLists.find(l => l.id === selectedList);
    return list ? `${list.name} (${list.language_name})` : 'Wybierz listę słówek';
  };

  const getSelectedTagName = () => {
    if (!selectedTag) return 'Wszystkie słówka';
    const tag = tags.find(t => t.id === selectedTag);
    return tag ? tag.name : 'Wszystkie słówka';
  };

  const getFilteredWordLists = () => {
    if (!excludeList) return wordLists;
    return wordLists.filter(list => list.id !== Number(excludeList));
  };

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="relative"
        ref={listDropdownRef}
      >
        <div 
          className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
          onClick={() => setIsListOpen(!isListOpen)}
        >
          <span className="text-sm">{getSelectedListName()}</span>
          <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {isListOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded shadow-lg max-h-60 overflow-auto">
            {getFilteredWordLists().map(list => (
              <div
                key={list.id}
                className="px-2 py-1 text-sm hover:bg-neutral-100 cursor-pointer"
                onClick={() => handleListSelect(list.id)}
              >
                {list.name} ({list.language_name})
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {!onlyList && tags.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="relative"
          ref={tagDropdownRef}
        >
          <div 
            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
            onClick={() => setIsTagOpen(!isTagOpen)}
          >
            <div className="flex items-center">
              {selectedTag ? (
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tags.find(t => t.id === selectedTag)?.color }}></div>
              ) : null}
              <span className="text-sm">{getSelectedTagName()}</span>
            </div>
            <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isTagOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded shadow-lg max-h-60 overflow-auto">
              <div 
                className="px-2 py-1 text-sm hover:bg-neutral-100 cursor-pointer flex items-center"
                onClick={() => handleTagSelect('')}
              >
                <span>Wszystkie słówka</span>
              </div>
              {tags.map(tag => (
                <div 
                  key={tag.id} 
                  className="px-2 py-1 text-sm hover:bg-neutral-100 cursor-pointer flex items-center"
                  onClick={() => handleTagSelect(tag.id)}
                >
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tag.color }}></div>
                  <span>{tag.name}</span>
                  <span className="ml-2 text-neutral-500">({tag.words_count})</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default ListAndTagDropdown; 